/**
 * Scope Selection Card Component
 * Displays a card for selecting between store or organization scope
 *
 * @module components/ScopeCard
 */

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// Types
import { ScopeCardProps } from './types';

// Styles
import useStyles from './styles';

/**
 * Scope Selection Card Component
 * Renders a clickable card for scope selection with icon, title, description and optional badge
 *
 * @param {ScopeCardProps} props - Component properties
 * @returns {React.ReactElement} Scope card component
 *
 * @example
 * // Primary color (recommended option)
 * <ScopeCard
 *   title="Organización"
 *   description="Edita los menús de manera centralizada"
 *   icon={<BusinessIcon />}
 *   onClick={() => handleScopeSelection(MENU_SCOPE.ORGANIZATION)}
 *   color="primary"
 *   showBadge={true}
 *   badgeText="Recomendado"
 * />
 *
 * // Default color (alternative option)
 * <ScopeCard
 *   title="Sucursal"
 *   description="Asigna áreas de cocina y publica menús"
 *   icon={<StoreIcon />}
 *   onClick={() => handleScopeSelection(MENU_SCOPE.STORE)}
 *   color="default"
 *   disabled={true}
 * />
 */
const ScopeCard = ({
  title,
  description,
  icon,
  onClick,
  color = 'default',
  disabled = false,
  showBadge = false,
  badgeText,
}: ScopeCardProps): React.ReactElement => {
  const classes = useStyles();

  // Build CSS classes - maintain color variant styles even when disabled
  let cardColorClass = classes.cardDefault;
  let iconColorClass = classes.iconDefault;
  let titleColorClass = '';
  let descriptionColorClass = '';

  // Apply color variant first
  if (color === 'primary') {
    cardColorClass = classes.cardPrimary;
    iconColorClass = classes.iconPrimary;
    titleColorClass = classes.cardTitlePrimary;
    descriptionColorClass = classes.cardDescriptionPrimary;
  }

  // Build classes combining variant + disabled state
  const cardClasses = [classes.card, cardColorClass, disabled ? classes.disabledCard : ''].filter(Boolean).join(' ');
  const iconClasses = `${classes.icon} ${iconColorClass}`;
  const titleClasses = `${classes.cardTitle} ${titleColorClass}`;
  const descriptionClasses = `${classes.cardDescription} ${descriptionColorClass}`;

  return (
    <Card className={cardClasses} onClick={() => !disabled && onClick()}>
      {/* Recommended Badge */}
      {showBadge && badgeText && (
        <Box className={classes.recommendedBadge}>
          <Typography variant="caption">{badgeText}</Typography>
        </Box>
      )}

      <CardContent className={classes.cardContent}>
        <Box className={classes.iconContainer}>
          <Box className={iconClasses}>{icon}</Box>
        </Box>
        <Typography className={titleClasses} component="div" variant="h6">
          {title}
        </Typography>
        <Typography className={descriptionClasses} color="text.secondary" variant="body2">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ScopeCard;
